import { useCallback, useEffect, useState } from 'react';
import { AlertType } from '../../../design-system/DSAlert';
import { useAlertContext } from '../../../contexts/AlertContext';
import useCopyToClipboard from '../../../hooks/useCopyToClipboard';
import useAnalytics from '../../../hooks/useAnalytics';
import { getScaledCloudinaryUrl } from '../../../utils';
import { useAffiliateLinkQuery } from './useAffiliateLinkQuery';

export const useAffiliateLinkState = (
  collectionId: string,
  hasAffiliateLink: boolean,
  cartImage?: string,
) => {
  const { showAlert } = useAlertContext();
  const { trackEvent } = useAnalytics();
  const { copy, copied, reset } = useCopyToClipboard();
  const { data, isError, isFetching, refetch } =
    useAffiliateLinkQuery(collectionId);

  const [isOpen, setIsOpen] = useState(false);

  const url = data?.url || null;
  const linkExists = hasAffiliateLink || !!url;
  const notReady = !cartImage;
  const scaledCartImage = cartImage
    ? getScaledCloudinaryUrl(cartImage, 600)
    : undefined;

  useEffect(() => {
    if (!scaledCartImage) return;
    const img = new Image();
    img.src = scaledCartImage;
  }, [scaledCartImage]);

  const errored =
    isOpen && !isFetching && !url && (isError || data !== undefined);
  const generating = isOpen && !linkExists && !url && !errored;
  const ready = isOpen && !generating && !errored;

  const openModal = useCallback(() => {
    if (notReady || isOpen) return;
    setIsOpen(true);
    reset();
    trackEvent({
      type: 'artist_edition_opened',
      context: `collectionId:${collectionId}`,
    });
    if (!url) void refetch();
  }, [notReady, isOpen, url, refetch, reset, trackEvent, collectionId]);

  const closeModal = useCallback(() => setIsOpen(false), []);

  const copyLink = useCallback(async () => {
    if (!url) return;
    const ok = await copy(url);
    if (ok) {
      trackEvent({
        type: 'artist_edition_link_copied',
        context: `collectionId:${collectionId}`,
      });
    }
    showAlert({
      message: ok ? 'Affiliate link copied' : 'Failed to copy link',
      alertType: ok ? AlertType.SUCCESS : AlertType.ERROR,
    });
  }, [url, copy, showAlert, trackEvent, collectionId]);

  const viewPage = useCallback(() => {
    if (url) {
      trackEvent({
        type: 'artist_edition_page_viewed',
        context: `collectionId:${collectionId}`,
      });
      window.open(url, '_blank', 'noopener');
    }
  }, [url, trackEvent, collectionId]);

  const retry = useCallback(() => void refetch(), [refetch]);

  return {
    isOpen,
    linkExists,
    notReady,
    generating,
    errored,
    ready,
    url,
    copied,
    scaledCartImage,
    openModal,
    closeModal,
    copyLink,
    viewPage,
    retry,
  };
};
